"use client";

import { useState } from "react";

import { FAQS } from "../_lib/faq";

/** Registration lives off-site, so its answer link has to leave the page. */
function isExternal(href: string) {
  return href.startsWith("http");
}

/**
 * The event FAQ. Answers stay in the DOM and are revealed by collapsing their
 * row, so they are still readable to anything that does not run the toggle.
 *
 * The answer sits outside the toggle rather than inside it: an answer can carry
 * a link on to the panel it is talking about, and a link inside a button is
 * neither valid nor clickable.
 */
export function FaqAccordion() {
  const [open, setOpen] = useState(0);

  return (
    <div className="border-marketing-border-light mt-10 overflow-hidden rounded-2xl border bg-white">
      <ul>
        {FAQS.map((faq, index) => {
          const isOpen = open === index;
          return (
            <li
              key={faq.question}
              className={[
                "group hover:bg-marketing-cream/60 relative transition-colors duration-200",
                index > 0 ? "border-marketing-border-light border-t" : "",
              ].join(" ")}
            >
              <span
                className={[
                  "bg-marketing-blue absolute top-0 bottom-0 left-0 w-[3px] origin-top transition-transform duration-300",
                  isOpen ? "scale-y-100" : "scale-y-0 group-hover:scale-y-100",
                ].join(" ")}
                aria-hidden="true"
              />

              <button
                type="button"
                onClick={() => {
                  setOpen(isOpen ? -1 : index);
                }}
                aria-expanded={isOpen}
                aria-controls={`convergence-faq-${index}`}
                className="focus-visible:outline-marketing-blue flex w-full items-start gap-6 px-6 py-5 text-left focus-visible:outline-2 focus-visible:-outline-offset-2"
              >
                <span
                  className={[
                    "flex-1 text-[15px] font-semibold transition-[color,translate] duration-200 group-hover:translate-x-0.5",
                    isOpen ? "text-marketing-blue" : "",
                  ].join(" ")}
                >
                  {faq.question}
                </span>
                <span
                  className={[
                    "mt-1 shrink-0 transition-transform duration-300",
                    isOpen ? "rotate-45" : "rotate-0",
                  ].join(" ")}
                  aria-hidden="true"
                >
                  <svg viewBox="0 0 12 12" className="size-3">
                    <path
                      d="M6 1v10M1 6h10"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      className={
                        isOpen
                          ? "text-marketing-blue"
                          : "text-marketing-text-muted"
                      }
                    />
                  </svg>
                </span>
              </button>

              {/* grid-rows is what lets this animate from nothing to auto */}
              <div
                id={`convergence-faq-${index}`}
                className="grid transition-[grid-template-rows,opacity] duration-300"
                style={{
                  gridTemplateRows: isOpen ? "1fr" : "0fr",
                  opacity: isOpen ? 1 : 0,
                }}
              >
                <div className="overflow-hidden">
                  <div className="px-6 pb-6">
                    <p className="text-marketing-text-muted max-w-[68ch] text-[14px] leading-6">
                      {faq.answer}
                    </p>
                    {faq.action ? (
                      <a
                        href={faq.action.href}
                        tabIndex={isOpen ? undefined : -1}
                        {...(isExternal(faq.action.href)
                          ? { target: "_blank", rel: "noopener noreferrer" }
                          : {})}
                        className="text-marketing-blue hover:text-marketing-blue-dark focus-visible:outline-marketing-blue group/link mt-3 inline-flex items-center gap-1.5 text-[14px] font-medium focus-visible:outline-2 focus-visible:outline-offset-4"
                      >
                        {faq.action.label}
                        <svg
                          viewBox="0 0 12 12"
                          className="size-3 transition-transform duration-200 group-hover/link:translate-x-0.5"
                          aria-hidden="true"
                        >
                          {isExternal(faq.action.href) ? (
                            <path
                              d="M2 10L10 2M4 2h6v6"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              fill="none"
                            />
                          ) : (
                            <path
                              d="M2 6h8M6.5 2.5 10 6l-3.5 3.5"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              fill="none"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          )}
                        </svg>
                      </a>
                    ) : null}
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
